import React, { useState, useMemo } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Box, Typography, Pressable } from '@/shared/design-system/primitives';
import { UnifiedTemporalPicker } from './UnifiedTemporalPicker';

interface CompressedTemporalBarProps {
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
    onUpdate: (updates: any) => void;
    onOpenPicker: (type: 'START' | 'END' | 'DURATION') => void;
}

/**
 * CompressedTemporalBar: Space-efficient temporal configuration.
 * V4.5: Delegates modal opening to parent for root-level Z-index hardening.
 */
export const CompressedTemporalBar: React.FC<CompressedTemporalBarProps> = ({
    startDate,
    startTime,
    endDate,
    endTime,
    onUpdate,
    onOpenPicker
}) => {
    // 1. DURATION LOGIC
    const durationMins = useMemo(() => {
        const start = new Date(`${startDate}T${startTime}`);
        const end = new Date(`${endDate}T${endTime}`);
        const diff = (end.getTime() - start.getTime()) / 60000;
        return isNaN(diff) ? 60 : Math.max(0, diff);
    }, [startDate, startTime, endDate, endTime]);

    const formatDuration = (totalMins: number) => {
        const h = Math.floor(totalMins / 60);
        const m = totalMins % 60;
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    };

    // 2. UPDATERS
    const updateDuration = (direction: number) => {
        let delta = 0;
        const current = durationMins;

        if (direction > 0) {
            if (current < 5) delta = 1;
            else if (current < 10) delta = 5 - (current % 5) || 5;
            else if (current < 30) delta = 5;
            else delta = 15 - (current % 15) || 15;
        } else {
            if (current <= 5) delta = -1;
            else if (current <= 30) delta = -(current % 5 || 5);
            else delta = -(current % 15 || 15);
        }

        const newDur = Math.max(0, current + delta);
        const start = new Date(`${startDate}T${startTime}`);
        const newEnd = new Date(start.getTime() + newDur * 60000);
        
        onUpdate({
            endDate: newEnd.getFullYear() + '-' + (newEnd.getMonth() + 1).toString().padStart(2, '0') + '-' + newEnd.getDate().toString().padStart(2, '0'),
            endTime: newEnd.getHours().toString().padStart(2, '0') + ':' + newEnd.getMinutes().toString().padStart(2, '0')
        });
    };

    const formatDateShort = (dateStr: string) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    };

    return (
        <Box style={{ borderBottom: '1px solid #222', backgroundColor: '#050505', padding: '15px 15px' }}>
            <Box style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                
                {/* START */}
                <Pressable onPress={() => onOpenPicker('START')} style={{ flex: 1, alignItems: 'center', paddingVertical: 10 }}>
                    <Typography style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: '900', color: '#fff' }}>{startTime}</Typography>
                    <span style={{ 
                        fontSize: '10px', 
                        fontWeight: '950', 
                        color: '#f29b11', 
                        textTransform: 'uppercase', 
                        marginTop: '4px', 
                        fontFamily: 'JetBrains Mono, monospace', 
                        fontVariantNumeric: 'tabular-nums',
                        letterSpacing: '1px'
                    }}>
                        {formatDateShort(startDate)}
                    </span>
                </Pressable>

                {/* DURATION */}
                <Box style={{ flex: 1.4, flexDirection: 'row', alignItems: 'center', height: 54, gap: 8 }}>
                    <Pressable onPress={() => updateDuration(-1)} style={{ width: 44, height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                        <Minus size={32} color="#ef4444" strokeWidth={3} />
                    </Pressable>
                    <Pressable onPress={() => onOpenPicker('DURATION')} style={{ flex: 1, alignItems: 'center' }}>
                        <Typography style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: '900', color: '#fff' }}>{formatDuration(durationMins)}</Typography>
                        <Typography style={{ fontSize: 8, fontWeight: '950', color: '#444', textTransform: 'uppercase', marginTop: 4 }}>DURATION</Typography>
                    </Pressable>
                    <Pressable onPress={() => updateDuration(1)} style={{ width: 44, height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                        <Plus size={32} color="#f29b11" strokeWidth={3} />
                    </Pressable>
                </Box>

                {/* END */}
                <Pressable onPress={() => onOpenPicker('END')} style={{ flex: 1, alignItems: 'center', paddingVertical: 10 }}>
                    <Typography style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: '900', color: '#fff' }}>{endTime}</Typography>
                    <span style={{ 
                        fontSize: '10px', 
                        fontWeight: '950', 
                        color: '#f29b11', 
                        textTransform: 'uppercase', 
                        marginTop: '4px', 
                        fontFamily: 'JetBrains Mono, monospace', 
                        fontVariantNumeric: 'tabular-nums',
                        letterSpacing: '1px'
                    }}>
                        {formatDateShort(endDate)}
                    </span>
                </Pressable>
            </Box>
        </Box>
    );
};
